import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PinIcon, StarIcon, SearchIcon, CloseIcon, FilterIcon, ChevronRightIcon, ChevronLeftIcon, CalendarIcon, UserIcon, PencilIcon } from "../site/icons.jsx";
import RangeCalendarField from "../site/RangeCalendar.jsx";
import {
  Panel, WherePanelContent, DatesPanelContent, GuestsPanelContent,
  useIsMobile, useScrollLock, makeDatePicker
} from "../site/SearchSheets.jsx";
import { iso, parseISO, addDays, nights as computeNights, fmtShort, fmtLong } from "../lib/dates.js";
import { HOTEL_BUCKET as HOTEL_TYPES, RENTAL_BUCKET as RENTAL_TYPES } from "../lib/hotelTypes.js";
import { ISLANDS, useSuggestions, slugifyName, islandSlug } from "../lib/search.js";
import { BRANDS, brandFor } from "../lib/brands.js";
import { stashOffer } from "../lib/rateCarry.js";
import { computeRate } from "../lib/rateMath.js";
import { lodgingNode, listOfferNode, itemListNode, useJsonLd, useHead, absUrl, CONTEXT, WEBSITE_ID, ORG_ID, ISLAND_NAMES } from "../lib/seo.js";
import { NAV, OFFER_TYPE, COMPOUND_TYPE, stayDateTime, money2 } from "../site/HotelMicrodata.jsx";

const IMAGE_ROOT = "https://images.aloharates.com";
// OUTRIGGER Reef has no 0.webp; use its existing first gallery photo.
const propertyImageUrl = id => `${IMAGE_ROOT}/properties/${id}/${id === "lp1b49f" ? 1 : 0}.webp`;
const PAGE_SIZE = 25;

const ISLAND_SLUG_TO_NAME = {
  oahu: "Oahu", maui: "Maui", kauai: "Kauai", "big-island": "Big Island",
  molokai: "Molokai", lanai: "Lanai"
};
const ISLAND_DISPLAY = {
  Oahu: "Oʻahu", Maui: "Maui", Kauai: "Kauaʻi", "Big Island": "Hawaiʻi (Big Island)",
  Molokai: "Molokaʻi", Lanai: "Lānaʻi"
};

// Fallback centers if the query doesn't hit any specific place
const ISLAND_CENTERS = {
  Oahu: { lat: 21.4389, lng: -158.0001, radius: 30000 },
  Maui: { lat: 20.7984, lng: -156.3319, radius: 40000 },
  Kauai: { lat: 22.0964, lng: -159.5261, radius: 30000 },
  "Big Island": { lat: 19.5429, lng: -155.6659, radius: 80000 },
  Molokai: { lat: 21.1444, lng: -157.0225, radius: 25000 },
  Lanai: { lat: 20.8283, lng: -156.9199, radius: 20000 }
};

function slugify(s) { return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }

function propertyHref(p, extra) {
  const q = extra?.toString();
  return `/properties/${p.id}-${slugify(p.name)}${q ? `?${q}` : ""}`;
}

// Build a place index from properties.json — unique { key: "area, island", lat, lng, count }.
// Median lat/lng of matching properties gives us a reasonable geo-center for /rates search.
function buildPlaceIndex(all) {
  const bucket = new Map();
  for (const p of all) {
    const area = p.area || p.city;
    if (!area || !p.lat || !p.lng) continue;
    const key = `${area}|${p.island}`;
    let row = bucket.get(key);
    if (!row) { row = { area, island: p.island, lats: [], lngs: [] }; bucket.set(key, row); }
    row.lats.push(p.lat);
    row.lngs.push(p.lng);
  }
  const list = [];
  for (const row of bucket.values()) {
    row.lats.sort((a, b) => a - b); row.lngs.sort((a, b) => a - b);
    const mid = Math.floor(row.lats.length / 2);
    list.push({
      area: row.area, island: row.island, count: row.lats.length,
      lat: row.lats[mid], lng: row.lngs[mid],
      // A rough radius: half of the diagonal of the bounding box, clamped
      radius: Math.max(3000, Math.min(30000, distanceMeters(row.lats[0], row.lngs[0], row.lats.at(-1), row.lngs.at(-1)) / 2 + 3000))
    });
  }
  return list.sort((a, b) => b.count - a.count);
}

function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000, toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// --- URL state helpers -----------------------------------------------------

function useQuery() {
  const [q, setQ] = useState(() => new URLSearchParams(location.search));
  useEffect(() => {
    const onPop = () => setQ(new URLSearchParams(location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const setParam = (updates) => {
    const next = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === "" || value == null) next.delete(key); else next.set(key, value);
    }
    history.replaceState(null, "", `${location.pathname}?${next}`);
    setQ(next);
  };
  return [q, setParam];
}

// --- Guest picker with per-child ages --------------------------------------

function GuestsPopover({ adults, children, setAdults, setChildren, onClose }) {
  const addChild = () => setChildren([...children, 8]);
  const removeChild = i => setChildren(children.filter((_, idx) => idx !== i));
  const changeAge = (i, v) => setChildren(children.map((a, idx) => idx === i ? v : a));
  return (
    <div className="p-4 space-y-4 w-72">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-ink">Adults</div>
          <div className="text-[11px] text-ink-muted">Ages 13+</div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setAdults(Math.max(1, adults - 1))} className="h-8 w-8 rounded-full border border-ink/15 text-ink flex items-center justify-center">−</button>
          <span className="w-4 text-center text-sm">{adults}</span>
          <button onClick={() => setAdults(Math.min(9, adults + 1))} className="h-8 w-8 rounded-full border border-ink/15 text-ink flex items-center justify-center">+</button>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium text-ink">Children</div>
            <div className="text-[11px] text-ink-muted">Ages 0–17</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => children.length && removeChild(children.length - 1)} className="h-8 w-8 rounded-full border border-ink/15 text-ink flex items-center justify-center">−</button>
            <span className="w-4 text-center text-sm">{children.length}</span>
            <button onClick={addChild} className="h-8 w-8 rounded-full border border-ink/15 text-ink flex items-center justify-center">+</button>
          </div>
        </div>
        {children.length > 0 && (
          <div className="space-y-2 mt-3 border-t border-ink/10 pt-3">
            {children.map((age, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <label className="text-xs text-ink-muted">Child {i + 1} age</label>
                <select value={age} onChange={e => changeAge(i, Number(e.target.value))} className="pill-input py-1 px-2 text-sm w-24">
                  {Array.from({ length: 18 }).map((_, n) => <option key={n} value={n}>{n < 1 ? "<1" : n}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
      <button onClick={onClose} className="btn-primary w-full py-2 text-sm">Done</button>
    </div>
  );
}

// --- Search bar (top of results page) --------------------------------------

// One editable row in the phone search editor.
function MobileField({ icon: Icon, label, value, onClick }) {
  return (
    <button type="button" onClick={onClick} className="w-full flex items-center gap-3 rounded-2xl border border-ink/15 bg-white px-4 py-3 text-left">
      <Icon size={18} className="text-ocean-600 shrink-0"/>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium text-ink-muted">{label}</div>
        <div className="truncate text-sm font-semibold text-ink">{value}</div>
      </div>
    </button>
  );
}

function SearchBar({ params, setParam, onSubmit }) {
  const today = useMemo(() => new Date(), []);
  const [q, setQ] = useState(params.get("q") || "");
  const [checkin, setCheckin] = useState(params.get("checkin") || iso(addDays(today, 14)));
  const [checkout, setCheckout] = useState(params.get("checkout") || iso(addDays(today, 17)));
  const [adults, setAdults] = useState(Number(params.get("adults")) || 2);
  const [children, setChildren] = useState(() => (params.get("children") || "").split(",").filter(Boolean).map(Number));
  const [showGuests, setShowGuests] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const guestBoxRef = useRef(null);
  const suggestBoxRef = useRef(null);

  useEffect(() => {
    const onDoc = e => {
      if (guestBoxRef.current && !guestBoxRef.current.contains(e.target)) setShowGuests(false);
      if (suggestBoxRef.current && !suggestBoxRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Phones show the current search as a single summary line and only open the
  // editor when it is tapped, the way every travel app does it — the wrapped
  // row of four pills ate a third of the screen above the results. `sheet`
  // drives the same full-screen panels the homepage hero search uses.
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(false);
  const [sheet, setSheet] = useState("");        // "where" | "dates" | "guests" | ""
  const [phase, setPhase] = useState("start");
  const [hover, setHover] = useState("");
  const [calMonth, setCalMonth] = useState(() => new Date((parseISO(checkin) || today).getFullYear(), (parseISO(checkin) || today).getMonth(), 1));
  useScrollLock(isMobile && !!sheet);

  const [pickedProperty, setPickedProperty] = useState(null);   // { id, island } when a property was picked
  const suggestions = useSuggestions(q, { enabled: showSuggest || sheet === "where" });
  const hasSuggestions = suggestions.islands.length + suggestions.places.length + suggestions.properties.length > 0;

  const pickDate = makeDatePicker({
    checkin, phase, setPhase, setHover,
    onChange: (ci, co) => { setCheckin(ci); setCheckout(co); }
  });
  const openDates = () => {
    setCalMonth(new Date((parseISO(checkin) || today).getFullYear(), (parseISO(checkin) || today).getMonth(), 1));
    setPhase(checkin && !checkout ? "end" : "start");
    setHover("");
    setSheet("dates");
  };

  const submit = (overrideQ) => {
    const submittedQ = overrideQ ?? q;
    // Every submit is a FRESH search — clear any leftover pin + resolve the
    // island from what the user just typed / picked. Otherwise a "Maui" search
    // silently stays scoped to the previous Big Island run.
    let nextIsland = "";
    let overrides = { pin: "" };
    if (pickedProperty) {
      nextIsland = islandSlug(pickedProperty.island) || "";
      overrides = { pin: pickedProperty.id };
    } else {
      const needle = submittedQ.trim().toLowerCase();
      const islandHit = ISLANDS.find(i =>
        i.value.toLowerCase() === needle ||
        i.display.toLowerCase() === needle ||
        i.slug === needle
      );
      if (islandHit) nextIsland = islandHit.slug;
    }
    setParam({
      q: submittedQ,
      checkin, checkout,
      adults: String(adults),
      children: children.length ? children.join(",") : "",
      island: nextIsland,
      ...overrides
    });
    onSubmit({
      q: submittedQ, checkin, checkout, adults, children,
      ...(pickedProperty ? { _islandOverride: pickedProperty.island, _pinId: pickedProperty.id } : {})
    });
    setShowSuggest(false);
    setShowGuests(false);
    setSheet("");
    setEditing(false);
  };

  const guestLabel = `${adults} adult${adults !== 1 ? "s" : ""}${children.length ? ` · ${children.length} child${children.length !== 1 ? "ren" : ""}` : ""}`;
  const whereLabel = q || "Anywhere in Hawaiʻi";
  const stayLabel = checkin && checkout ? `${fmtShort(checkin)} – ${fmtShort(checkout)}` : "Select dates";

  const sheets = (
    <>
      {sheet === "where" && (
        <Panel isMobile title="Where to?" onClose={() => setSheet("")} bodyClass="p-4">
          <WherePanelContent
            value={q}
            onChange={v => { setQ(v); setPickedProperty(null); }}
            onClear={() => { setQ(""); setPickedProperty(null); }}
            suggestions={suggestions}
            onIsland={i => { setQ(i.display); setPickedProperty(null); setSheet(""); }}
            onPlace={p => { setQ(p.area); setPickedProperty(null); setSheet(""); }}
            onProperty={p => { setQ(p.name); setPickedProperty({ id: p.id, island: p.island }); setSheet(""); }}
          />
        </Panel>
      )}

      {sheet === "dates" && (
        <Panel
          isMobile
          title="Select dates"
          onClose={() => setSheet("")}
          bodyClass="p-4"
          footer={
            <div className="flex items-center justify-between gap-3">
              <button type="button" onClick={() => { setCheckin(""); setCheckout(""); setPhase("start"); setHover(""); }} className="text-sm text-ink-muted hover:text-ink underline">Clear dates</button>
              <button type="button" onClick={() => setSheet("")} className="btn-primary py-3 px-8 text-sm" disabled={!checkin || !checkout}>
                {checkin && checkout ? "Done" : "Select a check-out date"}
              </button>
            </div>
          }
        >
          <DatesPanelContent
            today={today} checkin={checkin} checkout={checkout}
            phase={phase} hover={hover} setHover={setHover} onPick={pickDate}
            calMonth={calMonth} setCalMonth={setCalMonth}
          />
        </Panel>
      )}

      {sheet === "guests" && (
        <Panel
          isMobile
          title="Travelers"
          onClose={() => setSheet("")}
          bodyClass="p-4"
          footer={<button type="button" onClick={() => setSheet("")} className="btn-primary w-full py-3 text-base">Done</button>}
        >
          <GuestsPanelContent adults={adults} setAdults={setAdults} childAges={children} setChildAges={setChildren} maxAdults={9}/>
        </Panel>
      )}
    </>
  );

  if (isMobile) {
    return (
      <>
        {!editing ? (
          <button type="button" onClick={() => setEditing(true)} className="w-full flex items-center gap-3 rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-left shadow-sm">
            <SearchIcon size={18} className="text-ocean-600 shrink-0"/>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-ink">{whereLabel}</div>
              <div className="truncate text-xs text-ink-muted mt-0.5">{stayLabel} · {guestLabel}</div>
            </div>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink/10 text-ink-soft"><PencilIcon size={15}/></span>
          </button>
        ) : (
          <div className="space-y-2">
            <MobileField icon={PinIcon} label="Where to?" value={whereLabel} onClick={() => setSheet("where")}/>
            <MobileField icon={CalendarIcon} label="Dates" value={checkin && checkout ? `${fmtLong(checkin)} – ${fmtLong(checkout)}` : "Select dates"} onClick={openDates}/>
            <MobileField icon={UserIcon} label="Travelers" value={guestLabel} onClick={() => setSheet("guests")}/>
            <button type="button" onClick={() => submit()} className="btn-primary w-full py-3 text-base">Search</button>
            <button type="button" onClick={() => setEditing(false)} className="w-full py-2 text-sm font-medium text-ocean-700">Close</button>
          </div>
        )}
        {sheets}
      </>
    );
  }

  return (
    // md+: the wrapper draws the whole pill — border, white fill, rounded ends.
    // Each field is transparent there so its square background can't paint over
    // the wrapper's rounded corners, which is what clipped the left edge of the
    // border. Below md the fields are separate pills and own their own fill.
    <div className="flex flex-wrap items-stretch gap-2 md:gap-0 md:flex-nowrap md:rounded-pill md:border md:border-ink/10 md:bg-white md:overflow-visible md:shadow-sm w-full">
      {/* Where */}
      <div ref={suggestBoxRef} className="relative flex-1 min-w-[220px]">
        <div className="flex items-center gap-2 px-4 py-3 rounded-pill border border-ink/10 bg-white md:rounded-none md:border-0 md:border-r md:border-ink/10 md:bg-transparent h-full">
          <SearchIcon size={14} className="text-ink-muted shrink-0"/>
          <input
            value={q}
            onChange={e => { setQ(e.target.value); setShowSuggest(true); setPickedProperty(null); }}
            onFocus={() => setShowSuggest(true)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            placeholder="Where to? Try Waikīkī, Kāʻanapali, Kona"
            className="bg-transparent text-sm text-ink placeholder:text-ink-muted focus:outline-none w-full"
          />
        </div>
        {showSuggest && hasSuggestions && (
          <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-cardHover border border-ink/10 py-2 z-50 max-h-[28rem] overflow-y-auto">
            {suggestions.islands.length > 0 && (
              <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Islands</div>
            )}
            {suggestions.islands.map(i => (
              <button
                key={i.value}
                onClick={() => { setQ(i.display); setPickedProperty(null); setShowSuggest(false); }}
                className="w-full text-left px-4 py-2.5 hover:bg-sand-50 flex items-start gap-3"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ocean-50 text-ocean-600 mt-0.5"><PinIcon size={16}/></span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink">{i.display}</div>
                  <div className="text-[11px] text-ink-muted mt-0.5">{i.blurb}</div>
                </div>
              </button>
            ))}

            {suggestions.places.length > 0 && (
              <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Towns &amp; areas</div>
            )}
            {suggestions.places.map(s => (
              <button
                key={`${s.area}|${s.island}`}
                onClick={() => { setQ(s.area); setPickedProperty(null); setShowSuggest(false); }}
                className="w-full text-left px-4 py-2.5 hover:bg-sand-50 flex items-start gap-3"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ocean-50 text-ocean-600 mt-0.5"><PinIcon size={16}/></span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink">{s.area}</div>
                  <div className="text-[11px] text-ink-muted mt-0.5">{ISLAND_DISPLAY[s.island]} · {s.count.toLocaleString()} properties</div>
                </div>
              </button>
            ))}

            {suggestions.properties.length > 0 && (
              <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Properties</div>
            )}
            {suggestions.properties.map(p => (
              <button
                key={p.id}
                onClick={() => { setQ(p.name); setPickedProperty({ id: p.id, island: p.island }); setShowSuggest(false); }}
                className="w-full text-left px-4 py-2.5 hover:bg-sand-50 flex items-start gap-3"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sunset-50 text-sunset-500 text-base leading-none mt-0.5" aria-hidden>🏠</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink line-clamp-2 leading-snug">{p.name}</div>
                  <div className="text-[11px] text-ink-muted mt-0.5">Property · {[p.area || p.city, p.island].filter(Boolean).join(" · ")}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Dates — shared range calendar */}
      <RangeCalendarField
        checkin={checkin}
        checkout={checkout}
        onChange={(ci, co) => { setCheckin(ci); setCheckout(co); }}
      />

      {/* Guests */}
      <div ref={guestBoxRef} className="relative">
        <button
          onClick={() => setShowGuests(v => !v)}
          className="flex items-center gap-2 px-4 py-3 rounded-pill border border-ink/10 bg-white md:rounded-none md:border-0 md:border-r md:border-ink/10 md:bg-transparent w-full h-full text-left"
        >
          <UserIcon size={14} className="text-ink-muted shrink-0"/>
          <div className="flex flex-col">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Travelers</span>
            <span className="text-sm text-ink">{guestLabel}</span>
          </div>
        </button>
        {showGuests && (
          <div className="absolute right-0 top-full mt-2 bg-white rounded-2xl shadow-cardHover border border-ink/10 z-50">
            <GuestsPopover adults={adults} children={children} setAdults={setAdults} setChildren={setChildren} onClose={() => setShowGuests(false)}/>
          </div>
        )}
      </div>

      {/* Submit */}
      <button
        onClick={() => submit()}
        className="btn-primary rounded-pill md:rounded-l-none md:rounded-r-pill px-6 shrink-0"
      >Search</button>
    </div>
  );
}

// --- Result card (with real prices) ----------------------------------------

function ResultCard({ property, price, params, onHover, hoverActive, priceMode, tagged }) {
  const bookingParams = new URLSearchParams();
  ["checkin","checkout","adults","children"].forEach(k => { const v = params.get(k); if (v) bookingParams.set(k, v); });
  const showTotal = priceMode === "total";
  // Exactly one card on this page carries Hotel Center markup: the itinerary
  // Google sent the shopper to. It identifies the hotel and the stay, and its
  // link is the crawler's critical path onward to the property page, where the
  // full priced offer lives. Tagging every card would give the crawler many
  // offers to choose from, which the spec forbids.
  const ci = params.get("checkin") || "";
  const co = params.get("checkout") || "";
  const adults = Math.max(1, Number(params.get("adults")) || 2);
  const childCount = (params.get("children") || "").split(",").filter(Boolean).length;
  return (
    <a
      href={propertyHref(property, bookingParams)}
      target="_blank"
      rel="noopener noreferrer"
      {...(tagged ? { itemScope: true, itemType: "https://schema.org/Hotel https://schema.org/LodgingReservation", ...NAV.click(1) } : {})}
      onMouseEnter={() => onHover?.(property.id)}
      onMouseLeave={() => onHover?.(null)}
      className={`card overflow-hidden flex flex-col sm:flex-row group transition ${hoverActive ? "shadow-cardHover ring-2 ring-ocean-400" : "hover:shadow-cardHover"}`}
    >
      {tagged && (
        <span hidden>
          <meta itemProp="identifier" content={property.id}/>
          <meta itemProp="name" content={property.name}/>
          {ci && <meta itemProp="checkinTime" content={ci}/>}
          {co && <meta itemProp="checkoutTime" content={co}/>}
          <meta itemProp="numAdults" content={String(adults)}/>
          {childCount > 0 && <meta itemProp="numChildren" content={String(childCount)}/>}
          <span itemProp="makesOffer" itemScope itemType={OFFER_TYPE}>
            {ci && <meta itemProp="checkinTime" content={stayDateTime(ci)}/>}
            {co && <meta itemProp="checkoutTime" content={stayDateTime(co, null, "11:00:00")}/>}
            {price?.total > 0 ? (
              <>
                <meta itemProp="availability" content="https://schema.org/InStock"/>
                <span itemProp="priceSpecification" itemScope itemType={COMPOUND_TYPE}>
                  <meta itemProp="price" content={money2(price.total)}/>
                  <meta itemProp="priceCurrency" content="USD"/>
                </span>
              </>
            ) : (
              <meta itemProp="availability" content="https://schema.org/SoldOut"/>
            )}
          </span>
        </span>
      )}
      {/* Image column has a hard height cap so wildly-tall photos don't
          stretch the card. sm+: fixed 220px wide × 220px max tall. */}
      <div className="relative sm:w-56 shrink-0 aspect-[4/3] sm:aspect-auto sm:max-h-56 bg-sand-100 overflow-hidden">
        <img
          src={propertyImageUrl(property.id)}
          alt={property.name}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          onError={e => { e.currentTarget.style.display = "none"; }}
        />
      </div>
      <div className="p-4 md:p-5 flex flex-col flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] text-ink-muted flex items-center gap-1"><PinIcon size={11}/>{[property.area || property.city, property.island].filter(Boolean).join(", ")}</div>
            <h3 className="font-display text-lg leading-snug text-ink mt-0.5 truncate">{property.name}</h3>
          </div>
          {property.rating > 0 && (
            <div className="shrink-0 text-right">
              <div className="inline-flex items-center gap-1 rounded-lg bg-ocean-700 text-white text-sm font-semibold px-2.5 py-1.5">
                {Number(property.rating).toFixed(1)}
              </div>
              {property.reviewCount > 0 && <div className="text-[11px] text-ink-muted mt-1">{property.reviewCount.toLocaleString()} reviews</div>}
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
          {price?.isRefundable && <span className="rounded-pill bg-ocean-50 text-ocean-700 font-medium px-2 py-0.5">Free cancellation</span>}
          {price?.hasBreakfast && <span className="rounded-pill bg-sunset-50 text-sunset-600 font-medium px-2 py-0.5">Breakfast included</span>}
          {property.hasBeachfront && <span className="rounded-pill bg-sand-100 text-ink-soft px-2 py-0.5">Beachfront</span>}
          {property.hasPool && <span className="rounded-pill bg-sand-100 text-ink-soft px-2 py-0.5">Pool</span>}
          {property.hasWifi && <span className="rounded-pill bg-sand-100 text-ink-soft px-2 py-0.5">Wi-Fi</span>}
          {property.hasKitchen && <span className="rounded-pill bg-sand-100 text-ink-soft px-2 py-0.5">Kitchen</span>}
          {property.hasPetsAllowed && <span className="rounded-pill bg-sand-100 text-ink-soft px-2 py-0.5">Pet friendly</span>}
        </div>
        <div className="mt-auto pt-3 flex items-baseline justify-between">
          <div>
            {price ? (
              showTotal ? (
                <>
                  <div className="text-xl font-semibold text-ink">${price.total.toLocaleString()}<span className="text-[11px] font-normal text-ink-muted"> total · fees included</span></div>
                  <div className="text-[11px] text-ink-muted">for your dates</div>
                </>
              ) : (
                <>
                  <div className="text-lg font-semibold text-ink">${price.nightly.toLocaleString()}<span className="text-[11px] font-normal text-ink-muted"> / night</span></div>
                  <div className="text-[11px] text-ink-muted">${price.total.toLocaleString()} total · fees included</div>
                </>
              )
            ) : (
              <span className="text-xs text-ink-muted">See rates &amp; availability</span>
            )}
          </div>
          <span className="text-sm font-medium text-ocean-700 inline-flex items-center gap-1">View <ChevronRightIcon size={14}/></span>
        </div>
      </div>
    </a>
  );
}

// --- Google Maps -----------------------------------------------------------

function ResultsMap({ items, hoverId, setHoverId, params, center, priceMode }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markers = useRef({});
  const clusterer = useRef(null);
  const [ready, setReady] = useState(!!window.__GMAPS_READY__);
  const [noKey, setNoKey] = useState(false);

  useEffect(() => {
    if (window.__GMAPS_READY__) return setReady(true);
    if (!window.__GOOGLE_MAPS_KEY__ || window.__GOOGLE_MAPS_KEY__ === "YOUR_KEY") { setNoKey(true); return; }
    const on = () => setReady(true);
    window.addEventListener("gmaps:ready", on);
    return () => window.removeEventListener("gmaps:ready", on);
  }, []);

  useEffect(() => {
    if (!ready || !mapRef.current || mapInstance.current) return;
    const g = window.google;
    mapInstance.current = new g.maps.Map(mapRef.current, {
      center: center || { lat: 20.6, lng: -156.5 },
      zoom: 7,
      gestureHandling: "greedy",   // scroll to zoom without holding Ctrl
      disableDefaultUI: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        { featureType: "poi.business", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] }
      ]
    });
  }, [ready, center]);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const observer = new ResizeObserver(() => {
      if (mapInstance.current) window.google.maps.event.trigger(mapInstance.current, "resize");
    });
    observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, [ready]);

  useEffect(() => {
    if (!ready || !mapInstance.current) return;
    const g = window.google;
    // Clear existing markers + clusterer so a filter/sort change doesn't leave
    // ghost pins from the previous set on the map.
    if (clusterer.current) { clusterer.current.clearMarkers(); clusterer.current = null; }
    Object.values(markers.current).forEach(m => m.setMap(null));
    markers.current = {};

    const bounds = new g.maps.LatLngBounds();
    const bookingParams = new URLSearchParams();
    ["checkin","checkout","adults","children"].forEach(k => { const v = params.get(k); if (v) bookingParams.set(k, v); });

    const capped = items.slice(0, 250);
    let info = null;
    const markerList = [];
    for (const { property: p, price } of capped) {
      const lat = p.lat, lng = p.lng;
      if (!lat || !lng) continue;
      bounds.extend({ lat, lng });
      const label = price
        ? `$${Math.round(priceMode === "total" ? price.total : price.nightly).toLocaleString()}`
        : (p.rating ? Number(p.rating).toFixed(1) : "•");
      const marker = new g.maps.Marker({
        position: { lat, lng },
        // Don't attach directly — MarkerClusterer manages the map binding.
        label: { text: label, color: "#fff", fontSize: "12px", fontWeight: "600", fontFamily: "Inter, sans-serif" },
        icon: {
          path: g.maps.SymbolPath.CIRCLE,
          scale: 22,
          fillColor: "#0e5b71",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 2
        },
        title: p.name
      });
      markerList.push(marker);
      const href = propertyHref(p, bookingParams);
      const bodyHtml = `
        <a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="display:block;text-decoration:none;color:inherit;font-family:Inter,sans-serif;box-sizing:border-box;">
          <div style="height:clamp(90px,22dvh,150px);background:#f2ede4;overflow:hidden;">
            <img alt="${escapeHtml(p.name)}" src="${propertyImageUrl(p.id)}" style="display:block;width:100%;height:100%;object-fit:cover;" onerror="this.style.display='none'"/>
          </div>
          <div style="padding:12px 14px;">
            <div style="font-size:11px;color:#6b7883;">${escapeHtml([p.area || p.city, p.island].filter(Boolean).join(", "))}</div>
            <div style="font-family:Fraunces,serif;font-size:15px;color:#10233a;margin-top:2px;line-height:1.25;">${escapeHtml(p.name)}</div>
            ${price ? (priceMode === "total"
              ? `<div style="font-size:14px;color:#10233a;margin-top:6px;font-weight:600;">$${price.total.toLocaleString()}<span style="font-weight:400;color:#6b7883;font-size:11px;"> total · fees included</span></div>`
              : `<div style="font-size:14px;color:#10233a;margin-top:6px;font-weight:600;">$${price.nightly.toLocaleString()}<span style="font-weight:400;color:#6b7883;font-size:11px;"> / night</span></div>
                 <div style="font-size:11px;color:#6b7883;margin-top:2px;">$${price.total.toLocaleString()} total · fees included</div>`
            ) : `<div style="font-size:12px;color:#0e5b71;margin-top:6px;font-weight:600;">${params.get("checkin") ? "View availability" : "Select dates for prices"}</div>`}
            ${p.rating ? `<div style="font-size:12px;color:#10233a;margin-top:4px;">★ ${Number(p.rating).toFixed(1)} · ${(p.reviewCount || 0).toLocaleString()}</div>` : ""}
          </div>
        </a>`;
      marker.addListener("click", () => {
        if (info) info.close();
        const content = document.createElement("div");
        content.className = "map-property-card";
        content.innerHTML = bodyHtml;
        const close = document.createElement("button");
        close.type = "button";
        close.className = "map-property-close";
        close.setAttribute("aria-label", "Close property card");
        close.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>';
        close.addEventListener("click", () => info?.close());
        content.appendChild(close);
        info = new g.maps.InfoWindow({ content, maxWidth: 280, headerDisabled: true, ariaLabel: p.name });
        info.open({ anchor: marker, map: mapInstance.current });
      });
      marker.addListener("mouseover", () => setHoverId(p.id));
      marker.addListener("mouseout", () => setHoverId(null));
      markers.current[p.id] = marker;
    }

    // Hand every marker to MarkerClusterer so overlapping pins collapse to
    // a numbered cluster; zooming in breaks them apart into individual prices.
    if (window.markerClusterer && markerList.length) {
      clusterer.current = new window.markerClusterer.MarkerClusterer({
        map: mapInstance.current,
        markers: markerList,
        renderer: {
          render: ({ count, position }) => new g.maps.Marker({
            position,
            label: { text: String(count), color: "#fff", fontSize: "13px", fontWeight: "700", fontFamily: "Inter, sans-serif" },
            icon: {
              path: g.maps.SymbolPath.CIRCLE,
              scale: 24 + Math.min(count, 40) * 0.35,
              fillColor: "#ef7c3b",
              fillOpacity: 0.95,
              strokeColor: "#fff",
              strokeWeight: 2
            },
            zIndex: 900 + count
          })
        }
      });
    } else {
      // Fallback: MarkerClusterer script hasn't landed yet — attach directly.
      for (const m of markerList) m.setMap(mapInstance.current);
    }

    if (!bounds.isEmpty()) {
      mapInstance.current.fitBounds(bounds, 40);
      // Don't over-zoom on a lone marker
      g.maps.event.addListenerOnce(mapInstance.current, "idle", () => {
        if (mapInstance.current && mapInstance.current.getZoom() > 13) mapInstance.current.setZoom(13);
      });
    }
    return () => { if (info) info.close(); };
  }, [ready, items, params, setHoverId, priceMode]);

  // Hover from card → highlight marker
  useEffect(() => {
    if (!ready) return;
    Object.entries(markers.current).forEach(([id, m]) => {
      const active = id === hoverId;
      m.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: active ? 26 : 22,
        fillColor: active ? "#ef7c3b" : "#0e5b71",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2
      });
      m.setZIndex(active ? 999 : undefined);
    });
  }, [hoverId, ready]);

  if (noKey) return (
    <div className="h-full w-full flex items-center justify-center bg-sand-100 text-center p-6">
      <div>
        <div className="font-display text-lg text-ink">Map disabled</div>
        <p className="prose-body mt-2 text-sm">Add a Google Maps API key to <code className="text-xs bg-white px-1 py-0.5 rounded">production/index.html</code> to enable the map.</p>
      </div>
    </div>
  );
  return <div ref={mapRef} className="results-map h-full w-full"/>;
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]));
}

// --- Filter rail -----------------------------------------------------------

// Dual-thumb price slider. Slider bounds are LOCKED to the full rated set
// (passed via `bounds`) so dragging one thumb doesn't shrink the pool and
// snap the thumbs to the new min/max. Local lo/hi state is mirrored in refs
// so onMouseUp always commits the latest value even before React re-renders.
function PriceRangeFilter({ bounds, params, setParam }) {
  const dataMin = bounds.min || 0;
  const dataMax = Math.max(bounds.max || 1000, dataMin + 1);
  const paramMin = Number(params.get("priceMin")) || dataMin;
  const paramMax = Number(params.get("priceMax")) || dataMax;

  const [lo, setLo] = useState(paramMin);
  const [hi, setHi] = useState(paramMax);
  const loRef = useRef(paramMin);
  const hiRef = useRef(paramMax);
  const [minInput, setMinInput] = useState(paramMin > dataMin ? String(paramMin) : "");
  const [maxInput, setMaxInput] = useState(paramMax < dataMax ? String(paramMax) : "");

  // Reset local state when URL params change externally (e.g. Clear filters)
  useEffect(() => { setLo(paramMin); loRef.current = paramMin; setMinInput(paramMin > dataMin ? String(paramMin) : ""); }, [paramMin, dataMin]);
  useEffect(() => { setHi(paramMax); hiRef.current = paramMax; setMaxInput(paramMax < dataMax ? String(paramMax) : ""); }, [paramMax, dataMax]);

  const commit = () => {
    const l = loRef.current, h = hiRef.current;
    setParam({
      priceMin: l > dataMin ? String(l) : "",
      priceMax: h < dataMax ? String(h) : ""
    });
  };

  const onSliderLo = e => {
    const v = Math.min(Number(e.target.value), hiRef.current - 1);
    loRef.current = v; setLo(v);
  };
  const onSliderHi = e => {
    const v = Math.max(Number(e.target.value), loRef.current + 1);
    hiRef.current = v; setHi(v);
  };

  const commitInputs = () => {
    const l = Math.max(dataMin, Math.min(Number(minInput) || dataMin, dataMax - 1));
    const h = Math.max(l + 1, Math.min(Number(maxInput) || dataMax, dataMax));
    loRef.current = l; hiRef.current = h;
    setLo(l); setHi(h);
    commit();
  };

  const loPct = ((lo - dataMin) / (dataMax - dataMin)) * 100;
  const hiPct = ((hi - dataMin) / (dataMax - dataMin)) * 100;

  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-2">
        Nightly price
        <span className="ml-2 font-normal normal-case tracking-normal text-ink-muted">${lo} – ${hi}{hi >= dataMax ? "+" : ""}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number" min="0" placeholder="Min $" value={minInput}
          onChange={e => setMinInput(e.target.value)}
          onBlur={commitInputs}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commitInputs(); } }}
          className="pill-input px-3 py-1.5 text-sm w-1/2"
        />
        <span className="text-ink-muted">–</span>
        <input
          type="number" min="0" placeholder="Max $" value={maxInput}
          onChange={e => setMaxInput(e.target.value)}
          onBlur={commitInputs}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commitInputs(); } }}
          className="pill-input px-3 py-1.5 text-sm w-1/2"
        />
      </div>
      <div className="relative mt-4 h-6">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 rounded-full bg-sand-200"/>
        <div className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-ocean-500" style={{ left: `${loPct}%`, right: `${100 - hiPct}%` }}/>
        <input type="range" min={dataMin} max={dataMax} value={lo}
          onChange={onSliderLo}
          onMouseUp={commit} onTouchEnd={commit} onKeyUp={commit}
          className="price-range absolute inset-0 w-full appearance-none bg-transparent"/>
        <input type="range" min={dataMin} max={dataMax} value={hi}
          onChange={onSliderHi}
          onMouseUp={commit} onTouchEnd={commit} onKeyUp={commit}
          className="price-range absolute inset-0 w-full appearance-none bg-transparent"/>
      </div>
    </div>
  );
}

function FilterRail({ params, setParam, counts, priceBounds, isMobile, onClose }) {
  const propertyFacets = [
    { key: "beachfront", label: "Beachfront" },
    { key: "pool", label: "Pool" },
    { key: "hotTub", label: "Hot tub" },
    { key: "gym", label: "Gym / fitness" },
    { key: "restaurant", label: "Restaurant" },
    { key: "wifi", label: "Wi-Fi" },
    { key: "kitchen", label: "Kitchen" },
    { key: "grill", label: "BBQ grill" },
    { key: "terrace", label: "Balcony / terrace" },
    { key: "fireplace", label: "Fireplace" },
    { key: "parking", label: "Parking" },
    { key: "shuttle", label: "Airport shuttle" },
    { key: "petsAllowed", label: "Pet friendly" }
  ];
  const dealFacets = [
    { key: "breakfast", label: "Breakfast included" },
    { key: "refundable", label: "Free cancellation" }
  ];
  const minRating = Number(params.get("minRating") || 0);
  const sort = params.get("sort") || "reviews";
  const priceMode = params.get("priceMode") || "nightly";

  return (
    <aside className={`${isMobile ? "fixed inset-0 z-[70] bg-white overflow-y-auto" : ""} p-5 space-y-6 text-sm`}>
      {isMobile && (
        <div className="flex items-center justify-between border-b border-ink/10 pb-3">
          <div className="font-display text-lg text-ink">Filters</div>
          <button onClick={onClose} className="h-9 w-9 rounded-full border border-ink/15 flex items-center justify-center" aria-label="Close"><CloseIcon size={16}/></button>
        </div>
      )}

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-2">Sort by</div>
        <select value={sort} onChange={e => setParam({ sort: e.target.value })} className="pill-input px-3 py-2 text-sm w-full">
          <option value="price">Cheapest first</option>
          <option value="rating">Top rated</option>
          <option value="reviews">Most reviewed</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-2">Show prices as</div>
        <div className="grid grid-cols-2 rounded-pill bg-sand-100 p-1">
          {[{ v: "nightly", label: "Per night" }, { v: "total", label: "Total stay" }].map(opt => (
            <button
              key={opt.v}
              onClick={() => setParam({ priceMode: opt.v === "nightly" ? "" : opt.v })}
              className={`px-3 py-1.5 rounded-pill text-xs font-medium transition ${priceMode === opt.v ? "bg-white text-ink shadow-sm" : "text-ink-soft hover:text-ink"}`}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      <PriceRangeFilter bounds={priceBounds} params={params} setParam={setParam}/>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-2">Guest rating</div>
        <div className="space-y-1.5">
          {[9, 8, 7, 6, 0].map(v => (
            <label key={v} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="minRating" checked={minRating === v} onChange={() => setParam({ minRating: v || "" })} className="accent-ocean-600"/>
              <span className="text-ink">{v === 0 ? "Any rating" : `${v}+ · ${v >= 9 ? "Exceptional" : v >= 8 ? "Very good" : v >= 7 ? "Good" : "Pleasant"}`}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-2">Deals &amp; policies</div>
        <div className="space-y-1.5">
          {dealFacets.map(f => (
            <label key={f.key} className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="flex items-center gap-2">
                <input type="checkbox" checked={params.get(f.key) === "1"} onChange={e => setParam({ [f.key]: e.target.checked ? "1" : "" })} className="accent-ocean-600 h-4 w-4"/>
                <span className="text-ink">{f.label}</span>
              </span>
              <span className="text-[11px] text-ink-muted">{(counts[f.key] || 0).toLocaleString()}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-2">Amenities</div>
        <div className="space-y-1.5">
          {propertyFacets.map(f => (
            <label key={f.key} className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="flex items-center gap-2">
                <input type="checkbox" checked={params.get(f.key) === "1"} onChange={e => setParam({ [f.key]: e.target.checked ? "1" : "" })} className="accent-ocean-600 h-4 w-4"/>
                <span className="text-ink">{f.label}</span>
              </span>
              <span className="text-[11px] text-ink-muted">{(counts[f.key] || 0).toLocaleString()}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-2">Brands</div>
        <div className="space-y-1.5">
          {BRANDS.filter(b => (counts.brands?.[b.key] || 0) > 0).map(b => {
            const selected = new Set((params.get("brands") || "").split(",").filter(Boolean));
            const checked = selected.has(b.key);
            const toggle = () => {
              if (checked) selected.delete(b.key); else selected.add(b.key);
              setParam({ brands: [...selected].join(",") });
            };
            return (
              <label key={b.key} className="flex items-center justify-between gap-2 cursor-pointer">
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={checked} onChange={toggle} className="accent-ocean-600 h-4 w-4"/>
                  <span className="text-ink">{b.label}</span>
                </span>
                <span className="text-[11px] text-ink-muted">{counts.brands[b.key].toLocaleString()}</span>
              </label>
            );
          })}
          {BRANDS.every(b => !(counts.brands?.[b.key])) && (
            <div className="text-[11px] text-ink-muted italic">None in current results</div>
          )}
        </div>
      </div>

      <button
        onClick={() => setParam({ minRating: "", beachfront: "", pool: "", wifi: "", kitchen: "", parking: "", petsAllowed: "", breakfast: "", refundable: "", type: "", priceMode: "", priceMin: "", priceMax: "", brands: "", hotTub: "", restaurant: "", grill: "", terrace: "", fireplace: "", shuttle: "", gym: "" })}
        className="text-sm text-ocean-700 font-medium underline underline-offset-2"
      >Clear all filters</button>

      {isMobile && (
        <div className="pt-3 sticky bottom-0 bg-white border-t border-ink/10 -mx-5 px-5 pb-2">
          <button onClick={onClose} className="btn-primary w-full">See results</button>
        </div>
      )}
    </aside>
  );
}

// --- Rate fetch ------------------------------------------------------------

function cheapestRate(hotel, stayNights) {
  // Score each rate by the SAME numbers we'll display on the property page —
  // otherwise the results-card price and the property-page price diverge and
  // the carry-over looks broken.
  let bestRate = null, bestRoomType = null, bestPriced = null;
  for (const rt of hotel.roomTypes || []) {
    for (const rate of rt.rates || []) {
      const priced = computeRate(rate, stayNights);
      if (!priced.baseRate) continue;
      if (!bestPriced || priced.nightly < bestPriced.nightly) {
        bestRate = rate; bestRoomType = rt; bestPriced = priced;
      }
    }
  }
  if (!bestRate) return null;
  const board = (bestRate.boardType || "").toUpperCase();
  const hasBreakfast = ["BB", "HB", "FB", "AI"].includes(board);
  const refundTag = (bestRate.cancellationPolicies?.refundableTag || bestRate.refundableTag || "").toUpperCase();
  const isRefundable = refundTag === "RFN" || refundTag === "FREECANCELLATION";
  return {
    total: bestPriced.totalPrice,
    nightly: bestPriced.nightly,
    hasBreakfast,
    isRefundable,
    _rate: bestRate,
    _roomType: bestRoomType
  };
}

// Ad landing URLs don't carry a date — they carry year, month and day as
// separate tokens the ad network glues together at click time, so the dates
// arrive in whatever shape that produced. Tidy them into real ISO dates; the
// caller writes them back to the address bar, which is the URL the shopper
// actually looks at once the page loads.
function normalizeStayDates(params) {
  const clean = v => {
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(v || "").trim());
    if (!m) return null;
    const [, y, rawMonth, rawDay] = m;
    const month = Number(rawMonth), day = Number(rawDay);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${y}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  const checkin = clean(params.get("checkin"));
  let checkout = clean(params.get("checkout"));
  // Nights is the itinerary we actually quoted in the price feed, so it stands
  // in when checkout arrives missing or impossible.
  const stay = Number(params.get("nights"));
  if (checkin && stay > 0 && (!checkout || checkout <= checkin)) {
    checkout = iso(addDays(parseISO(checkin), stay));
  }
  return { checkin, checkout };
}

// --- Main page -------------------------------------------------------------

export default function ResultsPage() {
  const [params, setParam] = useQuery();
  const [all, setAll] = useState(null);
  const [rateMap, setRateMap] = useState({});     // hotelId -> {nightly, total}
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [mobileMap, setMobileMap] = useState(false);
  useScrollLock(mobileMap || filterOpen);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reset = () => { if (desktop.matches) { setMobileMap(false); setFilterOpen(false); } };
    desktop.addEventListener("change", reset);
    return () => desktop.removeEventListener("change", reset);
  }, []);
  useEffect(() => {
    const close = e => {
      if (e.key !== "Escape") return;
      if (filterOpen) setFilterOpen(false);
      else if (mobileMap) setMobileMap(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [filterOpen, mobileMap]);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [center, setCenter] = useState(null);

  useEffect(() => {
    fetch("/data/properties.json").then(r => r.ok ? r.json() : []).then(setAll).catch(() => setAll([]));
  }, []);

  const placeIndex = useMemo(() => all ? buildPlaceIndex(all) : [], [all]);
  const byId = useMemo(() => {
    const m = new Map();
    if (all) for (const p of all) m.set(p.id, p);
    return m;
  }, [all]);

  // Monotonic search id so a slow second pass never merges into a newer search.
  const searchSeq = useRef(0);

  const runRatesSearch = useCallback(async (input) => {
    if (!all) return;
    const { q, checkin, checkout, adults, children, _islandOverride, _pinId } = input;
    if (!checkin || !checkout || checkin >= checkout) return;
    const seq = ++searchSeq.current;

    // Resolve query → { lat, lng, radius, islandFilter }. Always island-scoped.
    let searchLat = null, searchLng = null, searchRadius = null, islandFilter = null;
    const needle = (q || "").trim().toLowerCase();

    // 0. Explicit island override (comes from picking a Property suggestion —
    //    the property's island is authoritative regardless of query text).
    if (_islandOverride && ISLAND_CENTERS[_islandOverride]) {
      const c = ISLAND_CENTERS[_islandOverride];
      searchLat = c.lat; searchLng = c.lng; searchRadius = c.radius; islandFilter = _islandOverride;
    }
    // 1. Query matches an island or a place → island-scoped
    if (searchLat == null && needle) {
      const islandKey = Object.keys(ISLAND_CENTERS).find(k => k.toLowerCase() === needle || ISLAND_DISPLAY[k].toLowerCase().includes(needle));
      if (islandKey) {
        const c = ISLAND_CENTERS[islandKey];
        searchLat = c.lat; searchLng = c.lng; searchRadius = c.radius; islandFilter = islandKey;
      } else {
        const match = placeIndex.find(p => p.area.toLowerCase().includes(needle));
        if (match) {
          islandFilter = match.island;
          const c = ISLAND_CENTERS[match.island];
          searchLat = c.lat; searchLng = c.lng; searchRadius = c.radius;
        }
      }
    }
    // 2. Fall back to `?island=` URL param
    if (searchLat == null) {
      const slug = params.get("island");
      const islandName = slug && ISLAND_SLUG_TO_NAME[slug];
      if (islandName && ISLAND_CENTERS[islandName]) {
        const c = ISLAND_CENTERS[islandName];
        searchLat = c.lat; searchLng = c.lng; searchRadius = c.radius; islandFilter = islandName;
      }
    }
    // 3. Final fallback: Oahu (busiest by traveler volume)
    if (searchLat == null) {
      const c = ISLAND_CENTERS.Oahu;
      searchLat = c.lat; searchLng = c.lng; searchRadius = c.radius; islandFilter = "Oahu";
    }
    setCenter({ lat: searchLat, lng: searchLng });

    setSearching(true); setSearchError(null);
    try {
      const body = {
        latitude: searchLat, longitude: searchLng, radius: searchRadius,
        checkin, checkout,
        occupancies: [{ adults, ...(children.length ? { children } : {}) }],
        currency: "USD", guestNationality: "US",
        limit: 200, maxRatesPerHotel: 1, includeHotelData: false,
        // roomMapping populates rate.mappedRoomId — required so the carry-over
        // into the property page can look up the room's name / photos / desc.
        roomMapping: true
      };
      const res = await fetch("/api/rates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      const stayNights = computeNights(checkin, checkout);
      const nextMap = {};
      for (const h of payload.data || []) {
        const price = cheapestRate(h, stayNights);
        if (!price) continue;
        // Stash the exact rate the user is about to see so the property page
        // can restore it if its own rate check comes back different / empty.
        stashOffer({
          hotelId: h.hotelId, checkin, checkout, adults, children,
          rate: price._rate, roomType: price._roomType, stayNights
        });
        // Only store the display-relevant fields in state — the raw rate lives
        // in localStorage now, keeping React state light.
        const { _rate, _roomType, ...displayPrice } = price;
        nextMap[h.hotelId] = { ...displayPrice, islandFilter };
      }
      setRateMap({ __islandFilter: islandFilter, ...nextMap });
      setSearching(false);

      // ── Second pass: vacation rentals only ─────────────────────────────
      // The hotel search above stays exactly as it was. This pass quietly
      // pulls in rentals it missed, using what actually gets rentals to price:
      //   • explicit hotelIds for every rental-bucket property on the island
      //     (the radius search checks only `limit` hotels, rentals lose out)
      //   • feed "LiteAPIGlobal" — the closed-user-group feed prices ~4x more
      //     rentals than the default feed (falls back to default if the key
      //     on this worker does not have that feed enabled)
      //   • roomMapping OFF — LiteAPI drops any rental whose rate it cannot
      //     map to a room (~20% of them). The property page already copes with
      //     a missing mappedRoomId.
      // Results are merged in only for properties the hotel pass did not
      // price, so nothing the user is already looking at changes.
      const rentalIds = all
        .filter(p => RENTAL_TYPES.has(p.hotelTypeId)
          && (islandFilter ? p.island === islandFilter
            : distanceMeters(searchLat, searchLng, p.lat, p.lng) <= searchRadius))
        .map(p => p.id);
      if (rentalIds.length) {
        try {
          const rentalBody = {
            hotelIds: rentalIds, checkin, checkout,
            occupancies: body.occupancies,
            currency: "USD", guestNationality: "US",
            limit: 5000, maxRatesPerHotel: 1, includeHotelData: false,
            roomMapping: false, timeout: 12
          };
          const post = (b) => fetch("/api/rates", {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(b)
          });
          let rentalRes = await post({ ...rentalBody, feed: "LiteAPIGlobal" });
          if (!rentalRes.ok) rentalRes = await post(rentalBody); // key without the feed → default feed
          if (rentalRes.ok && searchSeq.current === seq) {
            const rentalPayload = await rentalRes.json();
            const additions = {};
            for (const h of rentalPayload.data || []) {
              if (nextMap[h.hotelId]) continue; // hotel pass already priced it
              const price = cheapestRate(h, stayNights);
              if (!price) continue;
              stashOffer({
                hotelId: h.hotelId, checkin, checkout, adults, children,
                rate: price._rate, roomType: price._roomType, stayNights
              });
              const { _rate, _roomType, ...displayPrice } = price;
              additions[h.hotelId] = { ...displayPrice, islandFilter };
            }
            if (Object.keys(additions).length && searchSeq.current === seq) {
              setRateMap(prev => ({ ...additions, ...prev }));
            }
          }
        } catch {
          // Quiet by design: the hotel results are already on screen.
        }
      }
    } catch (err) {
      setSearchError("Could not load rates. Try again.");
    } finally {
      setSearching(false);
    }
  }, [all, placeIndex, params]);

  // Auto-run once on first load if URL has dates.
  //
  // `?checkin&checkout&pin=` with no island and no q is the ad landing-page
  // shape (Microsoft hotel ads, and anything else that sends a shopper to one
  // specific itinerary). The pinned property is authoritative for the island —
  // same rule as picking a property out of autosuggest — so resolve it here.
  // Without this the resolver falls through to its Oahu default and a Maui pin
  // gets searched against Oahu coordinates: the pinned hotel comes back with no
  // rate and the landing page shows a price that doesn't match the ad.
  //
  // The island is written back into the URL (replaceState, no history entry) so
  // the sidebar filter, the map and a copied URL all agree with the search that
  // actually ran.
  const didFirstFetch = useRef(false);
  useEffect(() => {
    if (didFirstFetch.current || !all) return;
    const { checkin, checkout } = normalizeStayDates(params);
    if (checkin && checkout) {
      didFirstFetch.current = true;
      const pinned = byId.get(params.get("pin") || "");
      const rewrite = {};
      if (pinned && !params.get("island")) {
        const slug = Object.keys(ISLAND_SLUG_TO_NAME).find(k => ISLAND_SLUG_TO_NAME[k] === pinned.island);
        if (slug) rewrite.island = slug;
      }
      if (checkin !== params.get("checkin")) rewrite.checkin = checkin;
      if (checkout !== params.get("checkout")) rewrite.checkout = checkout;
      if (Object.keys(rewrite).length) setParam(rewrite);
      runRatesSearch({
        q: params.get("q") || "",
        checkin,
        checkout,
        adults: Number(params.get("adults")) || 2,
        children: (params.get("children") || "").split(",").filter(Boolean).map(Number),
        ...(pinned ? { _islandOverride: pinned.island, _pinId: pinned.id } : {})
      });
    }
  }, [all, byId, runRatesSearch, params]);

  const activeTab = params.get("type") || "all";
  const q = (params.get("q") || "").trim().toLowerCase();
  const minRating = Number(params.get("minRating") || 0);
  const sort = params.get("sort") || "reviews";
  const islandSlug = params.get("island") || "";
  const ratedIds = useMemo(() => Object.keys(rateMap).filter(k => k !== "__islandFilter"), [rateMap]);
  const hasSearched = ratedIds.length > 0;

  // Slider bounds live on the ENTIRE rated set (not the currently-filtered
  // subset). Otherwise dragging a thumb shrinks the pool, min/max recompute,
  // and the thumbs snap to the new bounds instead of holding the user's value.
  const priceBounds = useMemo(() => {
    let lo = Infinity, hi = 0;
    for (const id of ratedIds) {
      const n = rateMap[id]?.nightly;
      if (!n) continue;
      if (n < lo) lo = n;
      if (n > hi) hi = n;
    }
    return { min: lo === Infinity ? 0 : Math.floor(lo), max: Math.ceil(hi) };
  }, [ratedIds, rateMap]);
  const islandFilterFromSearch = rateMap.__islandFilter;

  const items = useMemo(() => {
    if (!all) return [];

    // If a rate search has run, base list = ONLY properties that returned rates.
    // Otherwise show all matching properties (browse mode).
    let base;
    if (hasSearched) {
      base = ratedIds.map(id => byId.get(id)).filter(Boolean).map(p => ({
        property: p,
        price: {
          nightly: rateMap[p.id].nightly,
          total: rateMap[p.id].total,
          hasBreakfast: !!rateMap[p.id].hasBreakfast,
          isRefundable: !!rateMap[p.id].isRefundable
        }
      }));
    } else {
      base = all.map(p => ({ property: p, price: null }));
    }

    // Server-side geo already narrowed by island via the LiteAPI radius; still
    // let the user re-filter with the sidebar island selector.
    if (islandSlug && ISLAND_SLUG_TO_NAME[islandSlug]) {
      base = base.filter(({ property }) => property.island === ISLAND_SLUG_TO_NAME[islandSlug]);
    } else if (islandFilterFromSearch) {
      // Restrict to properties on the queried island so a "Waikiki" search doesn't
      // surface a stray Maui match from the fallback all-state pass
      base = base.filter(({ property }) => property.island === islandFilterFromSearch);
    }
    if (q && !hasSearched) base = base.filter(({ property }) =>
      (property.name || "").toLowerCase().includes(q) ||
      (property.area || "").toLowerCase().includes(q) ||
      (property.city || "").toLowerCase().includes(q));
    if (activeTab === "hotels") base = base.filter(({ property }) => HOTEL_TYPES.has(property.hotelTypeId));
    else if (activeTab === "rentals") base = base.filter(({ property }) => RENTAL_TYPES.has(property.hotelTypeId));
    if (minRating > 0) base = base.filter(({ property }) => (property.rating || 0) >= minRating);
    for (const k of ["beachfront","pool","wifi","kitchen","parking","petsAllowed"]) {
      if (params.get(k) === "1") {
        const key = "has" + k[0].toUpperCase() + k.slice(1);
        base = base.filter(({ property }) => property[key]);
      }
    }
    // Facility-ID amenities (LiteAPI facility_id list)
    const facilityFilters = [
      { key: "hotTub",    ids: [63] },
      { key: "restaurant",ids: [3] },
      { key: "grill",     ids: [72, 983, 984] },   // BBQ facilities / gas / charcoal
      { key: "terrace",   ids: [15, 118, 679] },   // Terrace / Sun terrace / Rooftop
      { key: "fireplace", ids: [225, 509, 1979] }, // Outdoor / Lobby fireplace
      { key: "shuttle",   ids: [17, 140, 594] },   // Airport / area shuttle
      { key: "gym",       ids: [492, 723, 537] }   // Fitness facilities
    ];
    for (const f of facilityFilters) {
      if (params.get(f.key) === "1") {
        base = base.filter(({ property }) => (property.facilityIds || []).some(id => f.ids.includes(id)));
      }
    }
    // Rate-derived filters (only meaningful once we have live rates)
    if (params.get("breakfast") === "1") base = base.filter(({ price }) => price?.hasBreakfast);
    if (params.get("refundable") === "1") base = base.filter(({ price }) => price?.isRefundable);
    const priceMin = Number(params.get("priceMin")) || 0;
    const priceMax = Number(params.get("priceMax")) || 0;
    if (priceMin || priceMax) {
      base = base.filter(({ price }) => {
        if (!price) return false;
        if (priceMin && price.nightly < priceMin) return false;
        if (priceMax && price.nightly > priceMax) return false;
        return true;
      });
    }
    const brandFilter = new Set((params.get("brands") || "").split(",").filter(Boolean));
    if (brandFilter.size) {
      base = base.filter(({ property }) => brandFilter.has(brandFor(property.name) || ""));
    }
    const sorted = [...base];
    if (sort === "price" && hasSearched) sorted.sort((a, b) => (a.price?.nightly ?? 1e12) - (b.price?.nightly ?? 1e12));
    else if (sort === "rating") sorted.sort((a, b) => (b.property.rating || 0) - (a.property.rating || 0) || (b.property.reviewCount || 0) - (a.property.reviewCount || 0));
    else if (sort === "reviews") sorted.sort((a, b) => (b.property.reviewCount || 0) - (a.property.reviewCount || 0));
    else if (sort === "name") sorted.sort((a, b) => (a.property.name || "").localeCompare(b.property.name || ""));
    // Fallback matches the default. It also catches "cheapest" in browse mode,
    // where no rates have loaded and a price sort would be an arbitrary order.
    else sorted.sort((a, b) => (b.property.reviewCount || 0) - (a.property.reviewCount || 0));

    // If a specific property was picked from search, pin it to the top of the list
    const pinId = params.get("pin");
    if (pinId) {
      const idx = sorted.findIndex(x => x.property.id === pinId);
      if (idx > 0) {
        const [pinned] = sorted.splice(idx, 1);
        sorted.unshift(pinned);
      } else if (idx === -1) {
        // Rates search didn't return the pinned property (sold out or filtered).
        // Still surface it at the top so users know they hit the right hotel.
        const prop = byId.get(pinId);
        if (prop) sorted.unshift({ property: prop, price: null, kind: "pinned-unavailable" });
      }
    }
    return sorted;
  }, [all, byId, hasSearched, ratedIds, rateMap, islandSlug, islandFilterFromSearch, q, activeTab, minRating, sort, params]);

  useEffect(() => { setVisible(PAGE_SIZE); }, [activeTab, islandSlug, minRating, sort, hasSearched]);

  const counts = useMemo(() => {
    const c = {
      beachfront: 0, pool: 0, wifi: 0, kitchen: 0, parking: 0, petsAllowed: 0,
      breakfast: 0, refundable: 0,
      hotTub: 0, restaurant: 0, grill: 0, terrace: 0, fireplace: 0, shuttle: 0, gym: 0,
      brands: {}, minPrice: Infinity, maxPrice: 0
    };
    const F = {
      hotTub: [63], restaurant: [3], grill: [72,983,984],
      terrace: [15,118,679], fireplace: [225,509,1979], shuttle: [17,140,594], gym: [492,723,537]
    };
    for (const { property: p, price } of items) {
      if (p.hasBeachfront) c.beachfront++;
      if (p.hasPool) c.pool++;
      if (p.hasWifi) c.wifi++;
      if (p.hasKitchen) c.kitchen++;
      if (p.hasParking) c.parking++;
      if (p.hasPetsAllowed) c.petsAllowed++;
      if (price?.hasBreakfast) c.breakfast++;
      if (price?.isRefundable) c.refundable++;
      const fids = p.facilityIds || [];
      for (const [k, ids] of Object.entries(F)) if (fids.some(id => ids.includes(id))) c[k]++;
      const brand = brandFor(p.name);
      if (brand) c.brands[brand] = (c.brands[brand] || 0) + 1;
      if (price?.nightly) {
        if (price.nightly < c.minPrice) c.minPrice = price.nightly;
        if (price.nightly > c.maxPrice) c.maxPrice = price.nightly;
      }
    }
    if (c.minPrice === Infinity) c.minPrice = 0;
    return c;
  }, [items]);

  const tabCounts = useMemo(() => {
    const source = hasSearched
      ? ratedIds.map(id => byId.get(id)).filter(Boolean)
      : (all || []);
    let base = source;
    if (islandSlug && ISLAND_SLUG_TO_NAME[islandSlug]) base = base.filter(p => p.island === ISLAND_SLUG_TO_NAME[islandSlug]);
    else if (islandFilterFromSearch) base = base.filter(p => p.island === islandFilterFromSearch);
    return {
      all: base.length,
      hotels: base.filter(p => HOTEL_TYPES.has(p.hotelTypeId)).length,
      rentals: base.filter(p => RENTAL_TYPES.has(p.hotelTypeId)).length
    };
  }, [all, byId, hasSearched, ratedIds, islandSlug, islandFilterFromSearch]);

  const shown = items.slice(0, visible);

  // JSON-LD: SearchResultsPage → ItemList of the visible stays, each typed
  // Hotel or Hotel + VacationRental, with the displayed "total · fees
  // included" price as a LodgingReservation offer once a rate search has run.
  const kindLabel = activeTab === "hotels" ? "Hotels" : activeTab === "rentals" ? "Vacation rentals" : "Hotels & vacation rentals";
  const whereLabel = ISLAND_NAMES[islandSlug] || ISLAND_NAMES[Object.keys(ISLAND_NAMES).find(k => ISLAND_SLUG_TO_NAME[k] === islandFilterFromSearch)] || "Hawaiʻi";
  const resultsJsonLd = shown.length ? (() => {
    const ci = params.get("checkin"), co = params.get("checkout");
    const list = shown.map(({ property, price }) => {
      const node = lodgingNode(property, { full: false });
      const offer = price?.total ? listOfferNode({ total: price.total, checkin: ci, checkout: co }) : undefined;
      if (offer) node.makesOffer = offer;
      return node;
    });
    return {
      "@context": CONTEXT,
      "@type": "SearchResultsPage",
      "@id": absUrl("/results/"),
      url: absUrl("/results/"),
      name: `${kindLabel} in ${whereLabel}`,
      isPartOf: { "@id": WEBSITE_ID },
      provider: { "@id": ORG_ID },
      mainEntity: itemListNode(list, { name: `${kindLabel} in ${whereLabel}` })
    };
  })() : null;
  useJsonLd("results-ldjson", resultsJsonLd);
  useHead({
    title: `${kindLabel} in ${whereLabel} — live rates | Aloha Rates`,
    description: `Compare ${kindLabel.toLowerCase()} in ${whereLabel} with live rates from Aloha Rates, the Hawaiʻi online travel agency. Totals shown with taxes and fees included.`,
    canonical: "/results/"
  });

  // Site header is h-20 (80px). Results shell locks to remaining viewport;
  // only the list column scrolls. Prevents dual scrollbars and lets the map
  // hold its full height instead of scrolling off the page.
  return (
    // Hotel Center can point a landing page URL at a search result as well as
    // a property, so this page declares itself a funnel stage too. The tagged
    // card's link is the critical path onward.
    <div className={`bg-sand-50 flex flex-col ${mobileMap ? "fixed inset-0 z-[60] h-dvh pt-[env(safe-area-inset-top)] lg:static lg:z-auto lg:h-[calc(100dvh-80px)] lg:pt-0" : "h-[calc(100dvh-80px)]"}`} {...NAV.landing}>
      {/* Top: search bar + tabs (flex-none so it doesn't grow) */}
      <div className="flex-none bg-white border-b border-ink/10">
        <div className={`${mobileMap ? "hidden lg:block" : ""} px-4 md:px-6 py-3`}>
          <SearchBar params={params} setParam={setParam} onSubmit={runRatesSearch}/>
        </div>
        <div className={`px-3 md:px-6 pb-3 flex items-center gap-2 ${mobileMap ? "pt-3 lg:pt-0" : ""}`}>
          <div role="group" aria-label="Property type" className="flex flex-1 min-w-0 lg:flex-none items-center gap-0.5 rounded-pill bg-sand-100 p-1">
            {[
              { key: "all", label: "All", count: tabCounts.all },
              { key: "hotels", label: "Hotels", count: tabCounts.hotels },
              { key: "rentals", label: "Vacation rentals", count: tabCounts.rentals }
            ].map(t => (
              <button
                key={t.key}
                aria-pressed={activeTab === t.key}
                aria-label={`${t.label}, ${t.count.toLocaleString()} properties`}
                onClick={() => setParam({ type: t.key === "all" ? "" : t.key })}
                className={`flex-1 lg:flex-none min-w-0 px-2 lg:px-4 min-h-11 lg:min-h-0 lg:py-1.5 rounded-pill text-xs lg:text-sm font-medium transition ${activeTab === t.key ? "bg-white text-ink shadow-sm" : "text-ink-soft hover:text-ink"}`}
              ><span className="lg:hidden">{t.key === "rentals" ? "Rentals" : t.label}</span><span className="hidden lg:inline">{t.label}</span> <span className="block lg:inline text-[10px] lg:text-sm text-ink-muted font-normal"><span className="hidden lg:inline">· </span>{t.count.toLocaleString()}</span></button>
            ))}
          </div>
          <button type="button" aria-label="Filters" onClick={() => setFilterOpen(true)} className="lg:hidden shrink-0 h-11 w-11 flex items-center justify-center rounded-full border border-ink/15 bg-white text-ink"><FilterIcon size={18}/></button>
          {mobileMap && <button type="button" aria-label="Close map" onClick={() => setMobileMap(false)} className="lg:hidden shrink-0 h-11 w-11 flex items-center justify-center rounded-full border border-ink/15 bg-white text-ink"><CloseIcon size={20}/></button>}
        </div>
        {(searching || searchError) && <div role="status" className="px-4 md:px-6 pb-3 text-xs text-ink-muted">{searchError || "Loading rates…"}</div>}
      </div>

      {/* Body: filter | list (scrolls) | map (locked) */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)_minmax(0,1fr)] overflow-hidden">
        <div className="hidden lg:block border-r border-ink/10 bg-white overflow-y-auto">
          <FilterRail params={params} setParam={setParam} counts={counts} priceBounds={priceBounds}/>
        </div>
        {filterOpen && <FilterRail params={params} setParam={setParam} counts={counts} priceBounds={priceBounds} isMobile onClose={() => setFilterOpen(false)}/>}

        <div className={`${mobileMap ? "hidden lg:block" : ""} p-4 md:p-5 space-y-3 overflow-y-auto scroll-hidden`}>
          {all === null || searching ? (
            <div className="space-y-3" {...NAV.loading}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card overflow-hidden flex">
                  <div className="w-64 h-40 bg-sand-200 animate-pulse"/>
                  <div className="p-4 flex-1 space-y-2">
                    <div className="h-3 w-1/3 bg-sand-200 rounded animate-pulse"/>
                    <div className="h-4 w-3/4 bg-sand-200 rounded animate-pulse"/>
                    <div className="h-3 w-1/2 bg-sand-200 rounded animate-pulse"/>
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="card p-10 text-center">
              <div className="font-display text-xl text-ink">{hasSearched ? "Nothing available for those dates" : "No matches"}</div>
              <p className="prose-body mt-2">Try shifting dates, changing guests, or widening the island.</p>
            </div>
          ) : (
            <>
              <div className="text-xs text-ink-muted px-1">
                {items.length.toLocaleString()} {activeTab === "hotels" ? "hotels" : activeTab === "rentals" ? "vacation rentals" : "places"}
                {hasSearched ? ` available ${fmtShort(params.get("checkin"))} – ${fmtShort(params.get("checkout"))}` : " · pick dates to see live rates"}
              </div>
              {shown.map(({ property, price }, i) => {
                const pinned = params.get("pin") === property.id;
                // The pinned property is the itinerary Google sent the shopper
                // to; with no pin, the first card stands in so the page always
                // offers the crawler exactly one path forward.
                const tagged = pinned || (!params.get("pin") && i === 0);
                return (
                  <div key={property.id} className="relative">
                    {pinned && (
                      <div className="absolute -top-2 left-4 z-10 rounded-pill bg-sunset-500 text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 shadow-sm">Your pick</div>
                    )}
                    <ResultCard property={property} price={price} params={params} onHover={setHoverId} hoverActive={hoverId === property.id} priceMode={params.get("priceMode") || "nightly"} tagged={tagged}/>
                  </div>
                );
              })}
              {visible < items.length && (
                <div className="pt-3 text-center">
                  <button onClick={() => setVisible(v => v + PAGE_SIZE)} className="btn-secondary">
                    Show more · {(items.length - visible).toLocaleString()} remaining
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className={`${mobileMap ? "" : "hidden lg:block"} h-full border-l border-ink/10 bg-sand-100`}>
          <ResultsMap items={items} hoverId={hoverId} setHoverId={setHoverId} params={params} center={center} priceMode={params.get("priceMode") || "nightly"}/>
        </div>
      </div>

      {!mobileMap && <button
        onClick={() => setMobileMap(true)}
        className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-40 rounded-pill bg-ink text-white px-5 py-2.5 text-sm font-medium shadow-cardHover"
      >Show map</button>}
    </div>
  );
}
